import { normalizeSpecialsDisplayName } from '@iheartjane/menu';
import { Flex, NugIcon, Typography } from '@iheartjane/reefer';
import { sentenceCase } from '@iheartjane/utils';
import { useLocation, useNavigate, useParams } from 'react-router';

import { useSearchContext } from '@jane/search/hooks';
import { useMenu } from '@jane/shared-ecomm/providers';
import { trackMenuTabTap } from '@jane/shared-ecomm/tracking';
import type { MenuCategoryPath } from '@jane/shared/types';

import { useGetPage } from '../../hooks/useGetPage';
import type { MenuCategory } from './menuCategoryTabs';
import {
  MenuTabIconContainer,
  StyledInnerItem,
  StyledListItem,
  StyledMenuTabButton,
} from './MenuTabButton.styles';
import { getTabTrackingSource } from './MenuTabs.util';

interface MenuTabButtonProps {
  isFirstTab: boolean;
  item: MenuCategory;
  name: MenuCategoryPath;
  width: number;
}

export const MenuTabButton = ({
  isFirstTab,
  item,
  name,
  width,
}: MenuTabButtonProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const page = useGetPage();
  const { category: categoryPath } = useParams<{
    category: MenuCategoryPath;
  }>();

  const { resetSearchState } = useSearchContext();

  const {
    appInfo: { appMode, isEmbed },
    paths,
    store,
  } = useMenu();
  const { Icon, color, iconColor } = item;

  const activeCategory = categoryPath === name || page === name;
  const isSelected =
    page === 'menu'
      ? isFirstTab
      : page === 'menuJaneGold'
        ? name === 'jane-gold'
        : !categoryPath && page !== 'specials'
          ? name === 'featured'
          : activeCategory;
  const isBrandSpecialsTab = name === 'jane-gold';

  const customLabels = store.custom_product_type_labels;
  const nameToRender =
    name === 'specials'
      ? normalizeSpecialsDisplayName(
          store.specials_row_configs?.custom_display_name
        )
      : isBrandSpecialsTab
        ? 'Jane Gold'
        : (customLabels && customLabels[name]) ||
          sentenceCase(name === 'all' ? 'all products' : name);

  const isOhioFlower = name === 'flower' && store.state === 'Ohio';
  const IconToRender = isOhioFlower ? NugIcon : Icon;
  const colorToRender = isOhioFlower ? 'palm-light' : color;

  const handleClick = () => {
    resetSearchState();
    const { city, state, name: storeName, id: storeId } = store;
    const source = getTabTrackingSource(location.pathname, categoryPath);

    trackMenuTabTap({
      appMode,
      productLocation: name,
      source,
      storeCity: city as string,
      storeState: state as string,
      storeId: String(storeId),
      storeName: storeName as string,
      storeCurrency: store.currency_code,
    });
    navigate(paths.menuCategory(name));
  };

  return (
    <StyledListItem isSelected={isSelected} aria-label={name}>
      <StyledInnerItem>
        <StyledMenuTabButton
          aria-label={`${nameToRender}, ${
            isSelected ? 'selected' : 'unselected'
          }`}
          name={name}
          color={isEmbed ? 'primary-light' : colorToRender}
          onClick={handleClick}
          width={width}
        >
          <MenuTabIconContainer>
            <Flex
              background={
                isSelected
                  ? isBrandSpecialsTab
                    ? 'gold-light'
                    : isEmbed
                      ? 'primary-light'
                      : colorToRender
                  : 'transparent'
              }
              height={40}
              width={40}
              alignItems="center"
              justifyContent="center"
            >
              <IconToRender
                className="menu-category--icon"
                color={
                  isSelected && isBrandSpecialsTab
                    ? 'gold'
                    : isEmbed
                      ? 'primary'
                      : iconColor
                        ? iconColor
                        : undefined
                }
                style={{
                  willChange: 'transform',
                  transition: !isSelected ? 'transform 150ms' : '',
                }}
              />
            </Flex>
          </MenuTabIconContainer>
          <Typography
            variant={isSelected ? 'body-bold' : 'body'}
            color={isSelected ? 'grays-black' : 'grays-mid'}
            whiteSpace="nowrap"
            truncateAt="100%"
          >
            {nameToRender}
          </Typography>
        </StyledMenuTabButton>
      </StyledInnerItem>
    </StyledListItem>
  );
};
